/* ============================================================
 * BetreuerSection – "Dein Betreuer-Team"
 * Design: Gold + Olive, analog zu MENTO Betreuer-Sektion
 * ============================================================ */
import { useState, useEffect, useRef } from "react";
import { useI18n } from "@/contexts/I18nContext";

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const BETREUER = [
  {
    nameKey: "landing.betreuer.lilliName",
    roleKey: "landing.betreuer.lilliRole",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/lilli-real_3821713a.png",
    descriptionKey: "landing.betreuer.lilliDesc",
    tagKeys: ["landing.betreuer.lilliTag1", "landing.betreuer.lilliTag2", "landing.betreuer.lilliTag3"],
  },
  {
    nameKey: "landing.betreuer.silkeName",
    roleKey: "landing.betreuer.silkeRole",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/silke-cropped_1f8d8adc.png",
    descriptionKey: "landing.betreuer.silkeDesc",
    tagKeys: ["landing.betreuer.silkeTag1", "landing.betreuer.silkeTag2", "landing.betreuer.silkeTag3"],
  },
  {
    nameKey: "landing.betreuer.susanName",
    roleKey: "landing.betreuer.susanRole",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/susan-betreuerin_f35687b6.png",
    descriptionKey: "landing.betreuer.susanDesc",
    tagKeys: ["landing.betreuer.susanTag1", "landing.betreuer.susanTag2", "landing.betreuer.susanTag3"],
  },
];

export default function BetreuerSection() {
  const { t } = useI18n();
  const { ref, visible } = useInView();

  return (
    <section
      id="betreuer"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ background: "oklch(0.97 0.005 80)" }}
      ref={ref}
    >
      {/* Subtle mandala background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-[0.03]">
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <circle cx="100" cy="100" r="90" fill="none" stroke="oklch(0.78 0.11 85)" strokeWidth="0.5" />
          <circle cx="100" cy="100" r="70" fill="none" stroke="oklch(0.78 0.11 85)" strokeWidth="0.5" />
          <circle cx="100" cy="100" r="50" fill="none" stroke="oklch(0.78 0.11 85)" strokeWidth="0.5" />
          {[...Array(12)].map((_, i) => (
            <line key={i} x1="100" y1="10" x2="100" y2="190" stroke="oklch(0.78 0.11 85)" strokeWidth="0.3" transform={`rotate(${i * 15} 100 100)`} />
          ))}
        </svg>
      </div>

      <div className="container relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span
            className="text-sm font-semibold tracking-[0.2em] uppercase"
            style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}
          >
            {t("landing.betreuer.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
            {t("landing.betreuer.heading")}
          </h2>
          <p
            className="text-lg mt-4 max-w-2xl mx-auto leading-relaxed"
            style={{ color: "oklch(0.45 0.02 80)", fontFamily: "var(--font-body)" }}
          >
            {t("landing.betreuer.description")}
          </p>
        </div>

        {/* Betreuer Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {BETREUER.map((person, i) => (
            <div
              key={i}
              className={`group rounded-3xl overflow-hidden bg-white border border-[oklch(0.92_0.01_80)] transition-all duration-700 hover:shadow-xl hover:border-[oklch(0.52_0.09_128/0.2)] ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Photo */}
              <div className="relative overflow-hidden aspect-[4/5]">
                <img
                  src={person.image}
                  alt={t(person.nameKey as any)}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(to bottom, transparent 60%, oklch(0.2 0.02 80 / 0.3) 100%)",
                  }}
                />
              </div>

              {/* Content */}
              <div className="p-6 lg:p-8">
                <h3 className="text-xl font-bold" style={{ color: "oklch(0.2 0.02 80)" }}>
                  {t(person.nameKey as any)}
                </h3>
                <p
                  className="text-sm font-semibold mt-1 mb-4"
                  style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}
                >
                  {t(person.roleKey as any)}
                </p>
                <p
                  className="text-sm leading-relaxed mb-5"
                  style={{ color: "oklch(0.45 0.02 80)", fontFamily: "var(--font-body)" }}
                >
                  {t(person.descriptionKey as any)}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {person.tagKeys.map((tagKey, j) => (
                    <span
                      key={j}
                      className="px-3 py-1.5 rounded-full text-xs font-medium"
                      style={{
                        background: "oklch(0.52 0.09 128 / 0.08)",
                        color: "oklch(0.42 0.09 128)",
                        border: "1px solid oklch(0.52 0.09 128 / 0.15)",
                      }}
                    >
                      {t(tagKey as any)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
